import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { api } from '../api';
import { errorMessage } from '../errors';
import NumberInput from '../shared/NumberInput';
import PhoneInput from '../shared/PhoneInput';
import PickSelect from '../shared/PickSelect';
import { phoneDigits } from '../shared/phone';

const blank = (v) => (v == null || String(v).trim() === '' ? null : String(v).trim());

// Ticket 202: edit a member's profile via PATCH /admin/users/{id}. Role and
// seat status have their own menu items; this form never sends them. Only a
// LO assistant carries a split and a supervising LO.
export default function EditMemberDialog({ member, members, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!member) return;
    setForm({
      name: member.name || '', nmls: member.nmls || '', phone: phoneDigits(member.phone),
      default_commission_bps: member.default_commission_bps ?? null,
      assistant_split_pct: member.assistant_split_pct ?? null,
      linked_lo_id: member.linked_lo_id || '',
    });
  }, [member]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isAssistant = member?.membership_role === 'loan_officer_assistant';
  const los = (members || []).filter((m) => ['loan_officer', 'admin', 'owner'].includes(m.membership_role) && (m.membership_status || 'active') === 'active');
  const phoneBad = !!form.phone && String(form.phone).length !== 10;
  const invalid = !String(form.name || '').trim() || phoneBad;

  // Only what the admin changed goes out, so a stale form never overwrites an edit made elsewhere
  // and a stored number that isn't plain US digits is left alone unless it is touched.
  const body = {};
  if (member) {
    const was = { name: member.name || '', nmls: member.nmls || '', phone: phoneDigits(member.phone), default_commission_bps: member.default_commission_bps ?? null };
    if (String(form.name || '').trim() !== was.name) body.name = String(form.name || '').trim();
    if ((blank(form.nmls) || '') !== was.nmls) body.nmls = blank(form.nmls);
    if ((form.phone || '') !== was.phone) body.phone = blank(form.phone);
    if ((form.default_commission_bps ?? null) !== was.default_commission_bps) body.default_commission_bps = form.default_commission_bps ?? null;
    if (isAssistant) {
      if ((form.assistant_split_pct ?? null) !== (member.assistant_split_pct ?? null)) body.assistant_split_pct = form.assistant_split_pct ?? null;
      if ((blank(form.linked_lo_id) || '') !== (member.linked_lo_id || '')) body.linked_lo_id = blank(form.linked_lo_id);
    }
  }
  const unchanged = Object.keys(body).length === 0;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${member.user_id}`, body);
      toast({ title: 'Profile saved', description: `${body.name || member.name || member.email} was updated.` });
      onSaved();
    } catch (e) {
      toast({ title: 'Could not save profile', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ color: '#070B5F' }} className="font-serif">Edit {member?.name || member?.email}</DialogTitle>
          <DialogDescription>Profile details follow this person into every workspace they belong to. This is audited.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name || ''} onChange={(e) => set('name')(e.target.value)} className="mt-1" data-testid="member-name" /></div>
          <div><Label>NMLS ID</Label><Input value={form.nmls || ''} onChange={(e) => set('nmls')(e.target.value)} className="mt-1" data-testid="member-nmls" /></div>
          <div>
            <Label>Phone</Label>
            <PhoneInput value={form.phone || ''} onChange={set('phone')} className="mt-1" data-testid="member-phone" />
            {phoneBad && <p className="text-xs text-red-600 mt-1">Enter all 10 digits, or leave it blank.</p>}
          </div>
          <div>
            <Label>Commission (basis points)</Label>
            <NumberInput integer min={0} value={form.default_commission_bps} onChange={set('default_commission_bps')} className="mt-1" data-testid="member-bps" />
            <p className="text-xs text-slate-500 mt-1">100 bps = 1% of the loan amount.</p>
          </div>
          {isAssistant && (
            <>
              <div>
                <Label>Assistant split</Label>
                <NumberInput min={0} max={100} value={form.assistant_split_pct} onChange={set('assistant_split_pct')} suffix="%" className="mt-1" data-testid="member-split" />
                <p className="text-xs text-slate-500 mt-1">Share of the loan officer&apos;s commission.</p>
              </div>
              <PickSelect label="Works for (loan officer)" value={form.linked_lo_id || ''} onChange={set('linked_lo_id')} testId="member-linked-lo"
                options={[{ value: '', label: 'No one' }, ...los.map((m) => ({ value: m.user_id, label: m.name || m.email }))]} />
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button onClick={save} disabled={busy || invalid || unchanged} className="btn-gold" data-testid="member-save">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
